import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import {
  LinearProgress as MuiLinearProgress,
  List,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { SidebarItemsType } from "../../types/sidebar";
import SidebarNavSection from "./SidebarNavSection";

import "../../vendor/perfect-scrollbar.css";
import SidebarAddFile from "./SidebarAddFileButton";
import { getNumFilesAndTotalFileSize } from "../../firebase/utils";
import { RootState } from "../../redux/store";
import useAppSelector from "../../hooks/useAppSelector";

const baseScrollbar = (props: any) => css`
  background-color: ${props.theme.sidebar.background};
  border-right: 1px solid rgba(0, 0, 0, 0.12);
  flex-grow: 1;
`;

const Scrollbar = styled.div`
  ${baseScrollbar}
`;

const PerfectScrollbar = styled(ReactPerfectScrollbar)`
  ${baseScrollbar}
`;

const Items = styled.div`
  padding-top: ${(props) => props.theme.spacing(2.5)};
  padding-bottom: ${(props) => props.theme.spacing(2.5)};
`;

const Storage = styled.div`
  width: 180px;
  margin-left: 45px;
  margin-top: 10px;
`;

const StorageText = styled(Typography)`
  margin-top: 3px;
  color: ${(props) => props.theme.sidebar.color};
`;

const LinearProgress = styled(MuiLinearProgress)`
  height: 7px;
  border-radius: 5px;
`;

type SidebarNavProps = {
  items: {
    title: string;
    pages: SidebarItemsType[];
  }[];
};

const SidebarNav: React.FC<SidebarNavProps> = ({ items }) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const state = useAppSelector((state: RootState) => state.main);
  const [numFiles, setNumFiles] = React.useState(0);
  const [spaceUsed, setSpaceUsed] = React.useState(0);
  const ScrollbarComponent = (
    matches ? PerfectScrollbar : Scrollbar
  ) as React.ElementType;

  useEffect(() => {
    (async () => {
      const { size, numFiles } = await getNumFilesAndTotalFileSize();
      setNumFiles(numFiles);
      setSpaceUsed(size);
    })();
  }, [state.files]);

  return (
    <ScrollbarComponent>
      <SidebarAddFile />
      <List disablePadding>
        <Items>
          {items &&
            items.map((item) => (
              <SidebarNavSection
                component="div"
                key={item.title}
                pages={item.pages}
                title={item.title}
              />
            ))}
          <Storage>
            <LinearProgress
              variant="determinate"
              value={(spaceUsed / 50) * 100}
              color={spaceUsed === 50 ? "error" : "primary"}
            />
            <StorageText>{spaceUsed.toFixed(2)}MB of 50MB used</StorageText>
          </Storage>
          <Storage>
            <LinearProgress
              variant="determinate"
              value={(numFiles / 10) * 100}
              color={numFiles === 10 ? "error" : "primary"}
            />
            <StorageText>{numFiles}/10 files uploaded</StorageText>
          </Storage>
        </Items>
      </List>
    </ScrollbarComponent>
  );
};

export default SidebarNav;
